import { Close, Folder } from '@mui/icons-material';
import {
  Box,
  Checkbox,
  Dialog,
  DialogActions,
  FormControl,
  IconButton,
  InputLabel,
  ListItemText,
  MenuItem,
  Select,
  SelectChangeEvent,
} from '@mui/material';
import { getSigners } from 'lib/api/business';
import { Signer } from 'lib/api/business/types';
import { updateTemplate } from 'lib/api/integrations/digitalSignature';
import CustomButton from 'lib/designSystem/Button';
import Title from 'lib/designSystem/Title';
import { errorHandler } from 'lib/functions/errors';
import { showAlert } from 'lib/functions/utils';
import React, { useState } from 'react';
import { useDispatch } from 'react-redux';

interface EditTemplateProps {
  open: boolean;
  setOpen: (open: boolean) => void;
  signers: Signer[];
  templateId?: string;
  refreshTemplates?: () => void;
}

const getUserLabel = (u: Signer) => {
  const displayName = u.name ?? u.email;
  return `${displayName}${u.name ? ` (${u.email})` : ''}`;
};

const EditTemplate = (props: EditTemplateProps) => {
  const { open, setOpen, signers, templateId, refreshTemplates } = props;
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);
  const { data: signersOptions } = getSigners(dispatch);
  const [selectedSigners, setSelectedSigners] = useState<number[]>(
    signers.map((u) => u.id),
  );

  const handleSignersChange = (event: SelectChangeEvent<number[]>) => {
    const value = event.target.value as number[];
    setSelectedSigners(value);
  };

  const handleSave = async () => {
    try {
      setLoading(true);
      if (!templateId) {
        showAlert(dispatch, 'error', 'Problemas obteniendo el portafolio');
        return;
      }
      await updateTemplate(dispatch, templateId, {
        signers: selectedSigners,
      });
      setOpen(false);
      refreshTemplates?.();
    } catch (error) {
      errorHandler(dispatch, error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={() => setOpen(false)} fullWidth maxWidth="xs">
      <Box
        sx={{
          px: 2.5,
          pt: 2,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'start',
        }}
      >
        <Title
          title="Editar plantilla de portafolio"
          icon={<Folder color="primary" sx={{ fontSize: 16 }} />}
          iconSize={30}
          titleSize="0.875rem"
          color="primary"
        />
        <IconButton aria-label="close" onClick={() => setOpen(false)}>
          <Close />
        </IconButton>
      </Box>
      <Box mx={2} mb={2}>
        <FormControl fullWidth margin="dense">
          <InputLabel id="own-signers-label">Firmantes</InputLabel>
          <Select
            labelId="own-signers-label"
            id="own-signers-select"
            multiple
            value={selectedSigners}
            label="Firmantes"
            onChange={handleSignersChange}
            renderValue={(selected) =>
              selected
                .map((id) => signersOptions?.find((u) => u.id === id))
                .filter((u): u is Signer => Boolean(u))
                .map((u) => getUserLabel(u))
                .join(', ')
            }
          >
            {signersOptions?.map((u) => (
              <MenuItem key={u.id} value={u.id}>
                <Checkbox checked={selectedSigners.indexOf(u.id) > -1} />
                <ListItemText primary={getUserLabel(u)} />
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>
      <DialogActions>
        <CustomButton
          variant="contained"
          color="primary"
          onClick={handleSave}
          loading={loading}
        >
          Guardar
        </CustomButton>
      </DialogActions>
    </Dialog>
  );
};

export default EditTemplate;
